/**
 * Hook to get all teams for the current coach from team_members table
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useCoachPreferences } from './useCoachPreferences';

export interface CoachTeamMembership {
  id: string;
  team_id: string;
  profile_id: string;
  role: 'owner' | 'coach' | 'assistant_coach' | 'analyst' | 'head_coach' | 'viewer';
  is_owner: boolean;
  created_at: string;
  team: {
    id: string;
    name: string;
    sport: string;
    division: string | null;
    gender: string | null;
    team_tier: string | null;
    organization_id: string | null;
    organization_program_id: string | null;
    current_season: string | null;
    created_at: string;
    is_demo?: boolean;
  };
}

export function useCoachTeams() {
  const { user } = useAuth();
  const { preferences } = useCoachPreferences();

  return useQuery({
    queryKey: ['coach-teams', user?.id, preferences.showDemoContent],
    queryFn: async (): Promise<CoachTeamMembership[]> => {
      if (!user?.id) return [];

      // Fetch user's teams (where they are a member)
      const { data: userTeams, error: userTeamsError } = await supabase
        .from('team_members')
        .select(
          `
          id,
          team_id,
          profile_id,
          role,
          is_owner,
          created_at,
          team:teams(
            id,
            name,
            sport,
            division,
            gender,
            team_tier,
            organization_id,
            organization_program_id,
            current_season,
            created_at,
            is_demo
          )
        `
        )
        .eq('profile_id', user.id)
        .in('role', ['owner', 'coach', 'assistant_coach', 'head_coach'])
        .order('created_at', { ascending: false });

      if (userTeamsError) throw userTeamsError;

      const userTeamMemberships = (userTeams || []) as unknown as CoachTeamMembership[];

      // Fetch demo team if preference allows
      if (preferences.showDemoContent) {
        const { data: demoTeam, error: demoTeamError } = await supabase
          .from('teams')
          .select(
            'id, name, sport, division, gender, team_tier, current_season, created_at, is_demo'
          )
          .eq('is_demo', true)
          .limit(1)
          .maybeSingle()
          .returns<{
            id: string;
            name: string;
            sport: string;
            division: string | null;
            gender: string | null;
            team_tier: string | null;
            current_season: string | null;
            created_at: string | null;
            is_demo: boolean;
          }>();

        if (demoTeamError) {
          console.error('[useCoachTeams] Error fetching demo team:', demoTeamError);
          // Don't throw - just return user teams if demo team fetch fails
        } else if (demoTeam) {
          // Create a "membership" object for demo team (user is not actually a member)
          // This allows us to display it in the teams list
          const demoMembership: CoachTeamMembership = {
            id: `demo-${demoTeam.id}`, // Synthetic ID
            team_id: demoTeam.id,
            profile_id: user.id, // User's ID for consistency, but they're not actually a member
            role: 'coach', // Placeholder role
            is_owner: false,
            created_at: demoTeam.created_at || new Date().toISOString(),
            team: {
              id: demoTeam.id,
              name: demoTeam.name,
              sport: demoTeam.sport,
              division: demoTeam.division,
              gender: demoTeam.gender,
              team_tier: demoTeam.team_tier ?? null,
              organization_id: null,
              organization_program_id: null,
              current_season: demoTeam.current_season,
              created_at: demoTeam.created_at || new Date().toISOString(),
              is_demo: true,
            },
          };

          // Return user teams first, then demo team
          return [...userTeamMemberships, demoMembership];
        }
      }

      return userTeamMemberships;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
